import { useQuery } from '@tanstack/react-query';
import { fetcher } from '@/lib/api';
import { useUserStore } from '@/store/useUserStore';

export interface LearnerStreak {
  current_streak: number;
  longest_streak: number;
  active_today: boolean;
  /** First day of the CURRENT streak (YYYY-MM-DD), null when the streak is 0. */
  start_date: string | null;
  /** Last day of the current streak (YYYY-MM-DD), null when the streak is 0. */
  end_date: string | null;
  last_active_date: string | null;
  total_active_days: number;
}

interface LearnerStreakResponse {
  success: boolean;
  data: LearnerStreak;
}

/**
 * Learner activity streak for the sidebar widget.
 *
 * The GET also records today as active server-side (idempotent — at most one
 * row per learner per day). The sidebar mounts on every portal page, so this
 * is what marks a learner present; see LearnerStreakController::show.
 *
 * Learner-only: admins/managers have no streak, so we don't spend a request on
 * them. `staleTime` is generous because the value can only change once a day.
 */
export const useLearnerStreak = () => {
  const role = useUserStore((state) => state.role);
  const isLearner = role === 'learner';

  return useQuery({
    queryKey: ['learner-streak'],
    queryFn: () => fetcher<LearnerStreakResponse>('/me/learning-streak'),
    enabled: isLearner,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    // An engagement widget must never retry-storm or surface an error boundary.
    retry: false,
    refetchOnWindowFocus: false,
  });
};
